import tour from "@/features/tour/tourService";

/**
 * Mixin que liga uma PÁGINA ao motor de tour.
 *
 * A página deve definir:
 *   tourId       -> String   (id único do tour, ex.: "agenda")
 *   tourSteps()  -> Array    ([{ anchor, title, description }])
 *
 * Comportamento:
 *   - Auto-inicia na PRIMEIRA visita do usuário (persistido em user_preferences).
 *   - Espera o conteúdo assíncrono da página renderizar antes de destacar.
 *   - Expõe startTour() para o botão "?" (replay a qualquer momento).
 *
 * Para páginas com carga assíncrona, defina tourReady() -> Boolean e o mixin
 * aguarda ficar true (ou o timeout) antes de auto-iniciar.
 */
export default {
  data() {
    return { _tourTimer: null, _tourTries: 0 };
  },

  mounted() {
    if (!this.tourId || typeof this.tourSteps !== "function") return;
    // Registra para o botão flutuante global poder reabrir este tour.
    tour.register(this.tourId, () => this.tourSteps());
    this.$nextTick(() => this._tourAutoStart());
  },

  beforeDestroy() {
    clearTimeout(this._tourTimer);
    if (this.tourId) tour.unregister(this.tourId);
    // Fecha o tour desta página ao sair, se ainda estiver ativo.
    if (tour.state.active && tour.state.tourId === this.tourId) tour.stop();
  },

  methods: {
    // Espera as âncoras existirem (conteúdo assíncrono) e então auto-inicia.
    async _tourAutoStart() {
      if (await tour.hasSeen(this.tourId)) return;
      this._tourWaitAndStart(/* auto */ true);
    },

    _tourWaitAndStart(auto) {
      clearTimeout(this._tourTimer);
      const ready =
        typeof this.tourReady === "function" ? this.tourReady() : true;
      const steps = this.tourSteps() || [];
      const hasAnchor = steps.some(
        (s) => !s.anchor || document.querySelector(`[data-tour="${s.anchor}"]`),
      );

      if (ready && hasAnchor) {
        if (auto) tour.maybeAutoStart(this.tourId, steps);
        else tour.start(this.tourId, steps);
        this._tourTries = 0;
        return;
      }
      // Ainda não pronto: tenta de novo por até ~6s.
      if (this._tourTries < 20) {
        this._tourTries += 1;
        this._tourTimer = setTimeout(() => this._tourWaitAndStart(auto), 300);
      }
    },

    /** Replay manual (botão de ajuda "?"). */
    startTour() {
      if (!this.tourId || typeof this.tourSteps !== "function") return;
      this._tourTries = 0;
      this._tourWaitAndStart(/* auto */ false);
    },
  },
};
